'use client'

import { useEffect, useState } from 'react'

import { useAuth } from '../contexts/AuthContext'
import { fetchGemPrices } from '../lib/gem-prices'
import { fetchPlanPrices } from '../lib/plan-prices'
import { gemSalePercent, offerFor, planSalePercent } from '../lib/sale'

// Experiment: does a promo convert better as a dismissible banner under the
// header (control), or folded into the header CTA itself as a badge (unified)?
//
// Phones only. The banner costs a whole strip of vertical chrome on a small
// screen and is dismissible; the badge is permanent but quiet. Both arms point
// at the same destination, so the comparison is surface, not offer.
// The promo that applies to this visitor, in the same precedence the header's
// banner selector has always used. Lifted out so both arms read one source —
// otherwise the badge and the banner could disagree about what's on offer.
//
// `label` is deliberately numeric ("-20%", "$4.99"): it has to fit a ~40px pill
// in every locale, and digits need no translation.
export function useActiveOffer() {
  const { displayPlan, user, authResolved } = useAuth()
  const sale = useSale(displayPlan, user, authResolved)
  return offerFor(displayPlan, sale.percent, sale.label)
}

// A running sale's percentage and label from v2's prices (plans for Free, gems for a paid plan), so the badge says
// "🎃 -30%" in a sale where it would say the standing "-20%". Both null outside a sale or without an answer.
function useSale(displayPlan, user, authResolved) {
  const kind = !displayPlan ? null : displayPlan === 'Free' ? 'plans' : 'gems'
  const [sale, setSale] = useState({ percent: null, label: null })
  useEffect(() => {
    if (!authResolved || !kind) return
    let live = true
    ;(async () => {
      const token = user ? await user.getIdToken() : null
      const answer =
        kind === 'plans'
          ? await fetchPlanPrices(token)
          : await fetchGemPrices(token)
      const percent =
        kind === 'plans' ? planSalePercent(answer) : gemSalePercent(answer)
      if (live) setSale({ percent, label: answer?.sale?.label ?? null })
    })()
    return () => {
      live = false
    }
  }, [user, authResolved, kind])
  return sale
}

// SETTLED 2026-08-12 — shipped `unified` on phones. The experiment ran 2026-08-01
// to 2026-08-12 (~164K users per arm) and found no significant difference on any
// metric: CTA clicks 2.81% vs 2.79% (p≈0.72), upgrade starts 0.809% vs 0.823%
// (p≈0.64), purchases 0.507% vs 0.484% (p≈0.39). At that base rate the test could
// only have resolved effects larger than ~14% relative, so read it as "no large
// difference" rather than "identical".
//
// With no measurable revenue difference, `unified` wins on cost: it drops a whole
// banner component and a strip of mobile vertical space. Note control was
// DIRECTIONALLY ahead on purchases (+4.6% relative, not significant) — if that
// turns out to be real, this is the line to revert.
//
// Desktop keeps the banner. It was never enrolled (the flag was only read below
// 768px), so there is no result for it and no basis for changing it here.
export function useOfferVariant() {
  const [state, setState] = useState({ variant: 'control', isPhone: false })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 767px)')

    const read = () => {
      // Settled: phones get unified, desktop keeps the banner.
      setState({
        variant: mq.matches ? 'unified' : 'control',
        isPhone: mq.matches,
      })
    }

    read()
    mq.addEventListener('change', read)

    return () => {
      mq.removeEventListener('change', read)
    }
  }, [])

  return state
}
